import { computed, Injectable, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  DynamicField,
  FieldType,
} from '@shared/components/dynamic-form/models/field';
import { FieldSettings } from '@shared/components/dynamic-form/models/field-base';
import { EditableForm } from '@shared/models/form';
import { generateUniqueId } from '@shared/utils/common.utils';
import deepClone from 'rfdc';

@Injectable({
  providedIn: 'root',
})
export class FormEditorService {
  private readonly _form = signal<EditableForm | null>(null);
  private readonly _selectedFieldId = signal<string | null>(null);
  private readonly _editable = signal(true);
  private readonly _scoreEnabled = signal(true);
  private readonly _cannotEditMessageKey = signal(
    'JuryForm.Message.CannotEdit'
  );

  readonly form = this._form.asReadonly();
  readonly selectedField = computed(() => {
    const form = this._form();
    return form
      ? form.fields.find(f => f.key === this._selectedFieldId())
      : undefined;
  });
  readonly editable = this._editable.asReadonly();
  readonly scoreEnabled = this._scoreEnabled.asReadonly();
  readonly cannotEditMessageKey = this._cannotEditMessageKey.asReadonly();

  constructor() {
    this.generateNewForm();
  }

  setScoreEnabled(scoreEnabled: boolean): void {
    this._scoreEnabled.set(scoreEnabled);
  }

  setCannotEditMessageKey(key: string): void {
    this._cannotEditMessageKey.set(key);
  }

  addField(fieldType: FieldType, index?: number): void {
    const newForm = deepClone()(this._form());
    const fields = newForm.fields;
    const newField = this.createField(fieldType);

    if (index === undefined) {
      fields.push(newField);
    } else {
      fields.splice(index, 0, newField);
    }

    newForm.fields = fields;

    this._form.set(newForm);
  }

  deleteField(fieldId: string): void {
    const form = this._form();
    const newForm = { ...form } as EditableForm;

    newForm.fields = newForm.fields.filter(field => field.key !== fieldId);

    this._form.set(newForm);
  }

  setSelectedFieldId(fieldId: string): void {
    this._selectedFieldId.set(fieldId);
  }

  updateField(fieldId: string, data: Partial<FieldSettings>) {
    const form = this._form();
    const newForm = { ...form } as EditableForm;

    newForm.fields = newForm.fields.map(field =>
      field.key === fieldId
        ? ({
            ...field,
            settings: { ...data },
            validatorOrOpts: data?.required ? Validators.required : null,
          } as DynamicField)
        : field
    );

    this._form.set(newForm);
  }

  setForm(form: EditableForm): void {
    const newForm = this.setFormValidators({ ...form });

    this._form.set(newForm);
  }

  generateNewForm(): void {
    this._form.set({
      key: 'form' + '-' + generateUniqueId(),
      fields: [],
    });
  }

  getFieldByKey(key: string): DynamicField {
    return this._form().fields.find(field => field.key === key);
  }

  setEditable(editable: boolean): void {
    this._editable.set(editable);
  }

  resetForm(): void {
    this._form.set(null);
  }

  setFormValidators(form: EditableForm): EditableForm {
    if (form?.fields?.length > 0) {
      for (const field of form.fields) {
        const validators = [];

        if (field.settings.required) {
          validators.push(Validators.required);
        }

        field.validatorOrOpts = validators.length ? validators : null;
      }
    }

    return form;
  }

  private createField(fieldType: FieldType): DynamicField {
    const field = {
      key: fieldType.controlType + '-' + generateUniqueId(),
      controlType: fieldType.controlType,
      settings: fieldType.fieldSettings,
      validatorOrOpts: fieldType.fieldSettings?.required
        ? Validators.required
        : null,
    } as DynamicField;

    return field;
  }
}
